import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Plus, Minus, Trash2, Search, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export const OrderEditItemsDialog = ({ order, onClose, onSaved }) => {
  const [items, setItems] = useState(() => order.items.map(i => ({ ...i })));
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then(res => {
      setProducts(res.data.filter(p => p.is_active !== false));
    }).catch(() => toast.error('Erro ao carregar produtos'));
  }, []);

  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return products.filter(p => p.name.toLowerCase().includes(term)).slice(0, 8);
  }, [search, products]);

  const changeQty = (idx, delta) => setItems(prev => prev
    .map((it, i) => i === idx ? { ...it, quantity: it.quantity + delta } : it)
    .filter(it => it.quantity > 0));

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const addProduct = (p) => {
    setItems(prev => {
      const idx = prev.findIndex(it => it.product_id === p.id && !it.variant_id && !(it.addons?.length));
      if (idx >= 0) return prev.map((it, i) => i === idx ? { ...it, quantity: it.quantity + 1 } : it);
      return [...prev, { product_id: p.id, product_name: p.name, quantity: 1, unit_price: p.promo_price || p.price, notes: '', addons: [] }];
    });
    setSearch('');
  };

  const subtotal = items.reduce((s, it) => s + it.unit_price * it.quantity, 0);
  const total = Math.max(0, subtotal - (order.discount || 0) + (order.delivery_fee || 0));

  const save = async () => {
    if (items.length === 0) {
      toast.error('O pedido precisa ter ao menos 1 item');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/orders/${order.id}/items`, { items });
      toast.success('Pedido atualizado');
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar itens');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#13131f] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-items-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Editar itens - Pedido #{order.order_number}</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            placeholder="Adicionar produto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-[#0f0f15] border-white/10 text-white"
            data-testid="edit-items-search"
          />
        </div>
        {results.length > 0 && (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {results.map(p => (
              <button
                key={p.id}
                onClick={() => addProduct(p)}
                className="w-full flex justify-between p-2 rounded-lg bg-[#0f0f15] hover:bg-[#1a1a2e] transition-colors text-sm"
                data-testid={`edit-items-result-${p.id}`}
              >
                <span className="text-white text-left">{p.name}</span>
                <span className="text-[#ff6b00] font-bold">R$ {(p.promo_price || p.price).toFixed(2)}</span>
              </button>
            ))}
          </div>
        )}

        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={`${it.product_id}-${idx}`} className="flex items-center gap-2 p-2 bg-[#0f0f15] rounded-lg" data-testid={`edit-item-row-${idx}`}>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{it.product_name}{it.variant_name ? ` (${it.variant_name})` : ''}</p>
                <p className="text-[#ff6b00] text-xs">R$ {(it.unit_price * it.quantity).toFixed(2)}</p>
              </div>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => changeQty(idx, -1)} data-testid={`edit-item-minus-${idx}`}><Minus className="w-4 h-4" /></Button>
              <span className="text-white w-6 text-center" data-testid={`edit-item-qty-${idx}`}>{it.quantity}</span>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => changeQty(idx, 1)} data-testid={`edit-item-plus-${idx}`}><Plus className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => removeItem(idx)} data-testid={`edit-item-remove-${idx}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-white/40 text-sm py-4">Nenhum item</p>}
        </div>

        <div className="bg-[#0f0f15] p-3 rounded-lg space-y-1 text-sm">
          <div className="flex justify-between text-white/60"><span>Subtotal</span><span>R$ {subtotal.toFixed(2)}</span></div>
          {order.delivery_fee > 0 && <div className="flex justify-between text-white/60"><span>Taxa de entrega</span><span>R$ {order.delivery_fee.toFixed(2)}</span></div>}
          {order.discount > 0 && <div className="flex justify-between text-green-400"><span>Desconto</span><span>-R$ {order.discount.toFixed(2)}</span></div>}
          <div className="flex justify-between font-bold text-base pt-1 border-t border-white/10">
            <span className="text-white">Novo total</span>
            <span className="text-[#ff6b00]" data-testid="edit-items-total">R$ {total.toFixed(2)}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={save} disabled={saving} data-testid="edit-items-save">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrderEditItemsDialog;
